import React from "react";

import { cx } from "~/tools/class-variance";
import { px } from "~/tools/px";

import styles from "./_sticky-frame.module.css";

export interface StickyFrameProps extends React.HTMLAttributes<HTMLDivElement> {
	/**
	 * The size of the sticky frames.
	 * The `number`s are interpreted as pixels, while `string`s are used as is.
	 */
	frames: [
		top: number | string,
		/**
		 * @default top
		 */
		right?: number | string,
		/**
		 * @default right
		 */
		left?: number | string,
	];

	/**
	 * A z-index value to assign to the sticky edges.
	 *
	 * @default 2
	 */
	zIndex?: number;
}

export const StickyFrame = React.forwardRef<HTMLDivElement, StickyFrameProps>(
	({ className, children, frames: [top, right = top, left = right], zIndex = 2, ...props }, ref) => {
		const style = {
			"--sticky-edge-z-index": zIndex,
			"--sticky-edge-top": px(top),
			"--sticky-edge-right": px(right),
			"--sticky-edge-left": px(left),
		} as React.CSSProperties;

		return (
			<div className={cx(styles.stickyFrame, className)} ref={ref} style={style} {...props}>
				<div className={styles.edgeTop} />
				<div className={styles.edgeLeft} />
				{children}
				<div className={styles.edgeRight} />
			</div>
		);
	},
);

if (import.meta.env.DEV) {
	StickyFrame.displayName = "StickyFrame";
}
